import { type DBSchema, openDB } from 'idb';

const DATABASE = 'imgenhancer-workspace-pending-files';
const STORE = 'pending-runs';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type WorkspaceTaskLocalFile = { inputSlotId: string; file: File };

/**
 * The upload contract that the Workspace captured for the staged files when
 * the quote was created. It is only a browser-side guard: the server
 * re-validates every bound file, so a tampered value can never grant access.
 */
export type WorkspaceTaskLocalFileUploadContract = {
  maxBytes: number;
  supportedMimeTypes: readonly string[];
  imageDimensionLimits?: {
    maxWidth?: number;
    maxHeight?: number;
    maxMegapixels?: number;
  };
};

export type WorkspaceTaskLocalFiles = {
  toolId: string;
  files: WorkspaceTaskLocalFile[];
  uploadContract: WorkspaceTaskLocalFileUploadContract;
};

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

const UPLOAD_CONTRACT_DIMENSION_KEYS = [
  'maxWidth',
  'maxHeight',
  'maxMegapixels',
] as const;

function parseWorkspaceTaskLocalFileUploadContract(
  value: unknown
): WorkspaceTaskLocalFileUploadContract | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (!isPositiveFiniteNumber(record.maxBytes)) return null;
  const supportedMimeTypes = record.supportedMimeTypes;
  if (
    !Array.isArray(supportedMimeTypes) ||
    supportedMimeTypes.length === 0 ||
    supportedMimeTypes.some(
      (item) => typeof item !== 'string' || item.trim().length === 0
    )
  ) {
    return null;
  }
  const rawLimits = record.imageDimensionLimits;
  let imageDimensionLimits:
    | WorkspaceTaskLocalFileUploadContract['imageDimensionLimits']
    | undefined;
  if (rawLimits !== undefined) {
    if (
      !rawLimits ||
      typeof rawLimits !== 'object' ||
      Array.isArray(rawLimits)
    ) {
      return null;
    }
    const limitsRecord = rawLimits as Record<string, unknown>;
    if (
      Object.keys(limitsRecord).some(
        (key) =>
          !UPLOAD_CONTRACT_DIMENSION_KEYS.includes(
            key as (typeof UPLOAD_CONTRACT_DIMENSION_KEYS)[number]
          )
      )
    ) {
      return null;
    }
    const limits: NonNullable<
      WorkspaceTaskLocalFileUploadContract['imageDimensionLimits']
    > = {};
    for (const key of UPLOAD_CONTRACT_DIMENSION_KEYS) {
      const candidate = limitsRecord[key];
      if (candidate === undefined) continue;
      if (!isPositiveFiniteNumber(candidate)) return null;
      limits[key] = candidate;
    }
    if (Object.keys(limits).length > 0) imageDimensionLimits = limits;
  }
  return {
    maxBytes: record.maxBytes,
    supportedMimeTypes: supportedMimeTypes.map((item) =>
      (item as string).trim()
    ),
    ...(imageDimensionLimits ? { imageDimensionLimits } : {}),
  };
}

type StoredRun = {
  pendingRunId: string;
  userId: string;
  origin: string;
  toolId: string;
  expiresAt: number;
  uploadContract: WorkspaceTaskLocalFileUploadContract;
  files: Array<{
    inputSlotId: string;
    blob: Blob;
    name: string;
    type: string;
    lastModified: number;
  }>;
};

interface PendingFilesSchema extends DBSchema {
  'pending-runs': { key: string; value: StoredRun };
}

async function openPendingFiles() {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
    throw new Error('Local task file storage is unavailable.');
  }
  return openDB<PendingFilesSchema>(DATABASE, 1, {
    upgrade(db) {
      db.createObjectStore(STORE, { keyPath: 'pendingRunId' });
    },
  });
}

export async function cleanupExpiredWorkspaceTaskLocalFiles() {
  const db = await openPendingFiles();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const now = Date.now();
    for (const run of await tx.store.getAll()) {
      if (run.expiresAt <= now) await tx.store.delete(run.pendingRunId);
    }
    await tx.done;
  } finally {
    db.close();
  }
}

export async function stageWorkspaceTaskLocalFiles(params: {
  pendingRunId: string;
  userId: string;
  toolId: string;
  files: WorkspaceTaskLocalFile[];
  expiresAt: number;
  uploadContract: WorkspaceTaskLocalFileUploadContract;
}): Promise<{ staged: true } | { staged: false }> {
  const now = Date.now();
  const uploadContract = parseWorkspaceTaskLocalFileUploadContract(
    params.uploadContract
  );
  if (
    !params.pendingRunId ||
    !params.userId ||
    typeof params.toolId !== 'string' ||
    params.toolId.trim().length === 0 ||
    params.files.length === 0 ||
    !Number.isFinite(params.expiresAt) ||
    params.expiresAt <= now ||
    !uploadContract ||
    params.files.some(
      ({ inputSlotId, file }) => !inputSlotId || !(file instanceof File)
    ) ||
    new Set(params.files.map(({ inputSlotId }) => inputSlotId)).size !==
      params.files.length
  ) {
    return { staged: false };
  }
  try {
    const db = await openPendingFiles();
    try {
      const tx = db.transaction(STORE, 'readwrite');
      for (const run of await tx.store.getAll()) {
        if (run.expiresAt <= now) await tx.store.delete(run.pendingRunId);
      }
      await tx.store.put({
        pendingRunId: params.pendingRunId,
        userId: params.userId,
        origin: window.location.origin,
        toolId: params.toolId.trim(),
        expiresAt: Math.min(params.expiresAt, now + MAX_AGE_MS),
        uploadContract,
        files: params.files.map(({ inputSlotId, file }) => ({
          inputSlotId,
          blob: file,
          name: file.name,
          type: file.type,
          lastModified: file.lastModified,
        })),
      });
      await tx.done;
      return { staged: true };
    } finally {
      db.close();
    }
  } catch {
    return { staged: false };
  }
}

export async function readWorkspaceTaskLocalFiles(params: {
  pendingRunId: string;
  userId: string;
}): Promise<WorkspaceTaskLocalFiles | null> {
  try {
    const db = await openPendingFiles();
    try {
      const run = await db.get(STORE, params.pendingRunId);
      if (!run) return null;
      if (run.expiresAt <= Date.now()) {
        await db.delete(STORE, params.pendingRunId);
        return null;
      }
      if (run.userId !== params.userId || run.origin !== window.location.origin)
        return null;
      const toolId = typeof run.toolId === 'string' ? run.toolId.trim() : '';
      if (!toolId) return null;
      const uploadContract = parseWorkspaceTaskLocalFileUploadContract(
        run.uploadContract
      );
      if (!uploadContract) return null;
      return {
        toolId,
        uploadContract,
        files: run.files.map(
          ({ inputSlotId, blob, name, type, lastModified }) => ({
            inputSlotId,
            file: new File([blob], name, { type, lastModified }),
          })
        ),
      };
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

export async function deleteWorkspaceTaskLocalFiles(pendingRunId: string) {
  const db = await openPendingFiles();
  try {
    await db.delete(STORE, pendingRunId);
  } finally {
    db.close();
  }
}

/**
 * Removes one locally staged slot after its upload is bound to the server
 * pending run. The containing record is dropped once no slot remains.
 */
export async function deleteWorkspaceTaskLocalFileSlot(params: {
  pendingRunId: string;
  inputSlotId: string;
}) {
  const db = await openPendingFiles();
  try {
    const run = await db.get(STORE, params.pendingRunId);
    if (!run) return;
    const files = run.files.filter(
      (file) => file.inputSlotId !== params.inputSlotId
    );
    if (files.length === run.files.length) return;
    if (files.length === 0) {
      await db.delete(STORE, params.pendingRunId);
      return;
    }
    await db.put(STORE, { ...run, files });
  } finally {
    db.close();
  }
}
